import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const CORRECT_PIN = '78451'; // In a real app, this should be stored securely on the server

const modalStyle = {
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: '#1f2937',
  padding: '2rem',
  borderRadius: '1rem',
  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  zIndex: 1000,
  width: '90%',
  maxWidth: '400px',
  border: '1px solid #4b5563',
  color: '#f3f4f6'
};

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.75)',
  zIndex: 999,
  backdropFilter: 'blur(4px)'
};

const PinInput = ({ length = 5, value = '', onChange }) => {
  const inputRefs = React.useRef([]);
  
  const handleChange = (e, index) => {
    const newValue = e.target.value.replace(/[^0-9]/g, '');
    if (newValue) {
      const newPin = value.split('');
      newPin[index] = newValue[newValue.length - 1];
      onChange(newPin.join('').substring(0, length));
      
      // Move to next input if not last
      if (index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !value[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={el => inputRefs.current[index] = el}
          type="password"
          className="w-12 h-14 text-2xl text-center bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all duration-200"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          autoFocus={index === 0}
        />
      ))}
    </div>
  );
};

export default function PinProtectedRoute({ allowedRoles, children }) {
  const [pin, setPin] = useState('');
  const [showPinModal, setShowPinModal] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  const handleClose = () => {
    navigate('/home'); // or any other route you want to redirect to
  };

  // Check if user is authenticated and has the right role
  if (!user) return <Navigate to="/login" />;
  if (!allowedRoles.map(r => r.toLowerCase()).includes(user.role.toLowerCase())) {
    return <Navigate to="/unauthorized" />;
  }

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pin === CORRECT_PIN) {
      setShowPinModal(false);
      setError('');
    } else {
      setError('Incorrect PIN. Please try again.');
      setPin('');
    }
  };

  if (showPinModal) {
    return (
      <>
        <div style={overlayStyle}></div>
        <div style={modalStyle}>
          <div className="relative">
            <button 
              onClick={handleClose}
              className="absolute -top-8 -right-2 text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-pink-500 rounded-full p-1 transition-colors duration-200"
              aria-label="Close"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold bg-gradient-to-r from-pink-500 to-pink-700 bg-clip-text text-transparent">Security Check</h3>
            <p className="text-gray-300 mt-2 mb-6">Enter the 5-digit PIN to continue</p>
          </div>
          <form onSubmit={handlePinSubmit}>
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-400 mb-4">Please enter the security PIN to access this page</label>
              <div className="mt-4">
                <PinInput 
                  length={5} 
                  value={pin}
                  onChange={setPin}
                />
              </div>
              {error && <div className="text-danger mt-2">{error}</div>}
            </div>
            <div className="d-flex justify-content-end">
              <button 
                type="submit" 
                className="w-full px-6 py-3 bg-gradient-to-r from-pink-600 to-pink-800 text-white font-semibold rounded-xl hover:from-pink-700 hover:to-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-200 shadow-lg hover:shadow-pink-500/20"
              >
                Verify PIN
              </button>
            </div>
          </form>
        </div>
      </>
    );
  }

  return children;
}
